import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Radio } from 'tea-component';
import SvgIcon from '@/pages/home/components/SvgIcon';
import CardCommon from '@op/components/CardCommon';
import useLanguage from '@op/hooks/useLanguage';
import type {
  IReportPermissionTarget,
  TPermissionLevel,
} from '@op/services/report';
import { PERMISSION_LEVEL_OPTIONS } from './constants';
import { useReportPermissionInfo } from './hooks/useReportPermissionInfo';
import { useSaveReportPermission } from './hooks/useSaveReportPermission';
import { useSearchPermissionUsers } from './hooks/useSearchPermissionUsers';
import PermissionUserInput from './PermissionUserInput';
import type { TReportPermissionNotificationMode } from './ReportPermissionProvider';
import './index.scss';

const INFO_LOADING_HEIGHT = 160;

export interface IReportAccessPanelProps {
  visible: boolean;
  target: IReportPermissionTarget | null;
  onClose: () => void;
  onSaved?: () => void;
  notificationMode?: TReportPermissionNotificationMode;
  onPermissionLinkClick?: (target: IReportPermissionTarget) => void;
}

export default function ReportAccessPanel({
  visible,
  target,
  onClose,
  onSaved,
  notificationMode = 'list',
  onPermissionLinkClick,
}: IReportAccessPanelProps) {
  const { formatMessage } = useLanguage();

  const {
    loading,
    permissionLevel,
    setPermissionLevel,
    selectedUsers,
    setSelectedUsers,
  } = useReportPermissionInfo(target, visible);

  const {
    searchOptions,
    searchLoading,
    searchUsers,
    clearSearch,
    loadMore,
    hasMore,
    loadMoreLoading,
  } = useSearchPermissionUsers();

  const { saving, save } = useSaveReportPermission({
    onClose,
    onSaved,
    notificationMode,
    onPermissionLinkClick,
  });

  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!visible) {
      clearSearch();
      setIsError(false);
    }
  }, [clearSearch, visible]);

  useEffect(() => {
    if (permissionLevel !== 'partial') {
      setIsError(false);
    }
  }, [permissionLevel]);

  const handleSelectedUsersChange = useCallback(
    (users: typeof selectedUsers) => {
      setSelectedUsers(users);
      if (users.length > 0) {
        setIsError(false);
      }
    },
    [setSelectedUsers],
  );

  const handleSave = () => {
    if (!target) return;

    if (permissionLevel === 'partial' && selectedUsers.length === 0) {
      setIsError(true);
      return;
    }

    void save({
      target,
      permissionLevel,
      selectedUsers,
    });
  };

  if (!visible) return null;

  return (
    <Modal
      className="dbo-report-access-modal"
      visible={visible}
      size="l"
      caption={formatMessage({ id: 'Access' })}
      onClose={onClose}
    >
      <Modal.Body>
        <CardCommon
          contentCard
          className="dbo-report-access-panel-card"
          loading={loading}
          loadingHeight={INFO_LOADING_HEIGHT}
        >
          <div className="dbo-report-access-panel">
            <Radio.Group
              className="dbo-report-access-panel__cards"
              value={permissionLevel}
              onChange={(value) =>
                setPermissionLevel(value as TPermissionLevel)
              }
            >
              {PERMISSION_LEVEL_OPTIONS.map((option) => (
                <Radio
                  key={option.value}
                  name={option.value}
                  className="dbo-report-access-panel__card"
                >
                  <div className="dbo-report-access-panel__content">
                    <SvgIcon
                      type={option.iconType}
                      className={`dbo-report-access-panel__card-icon ${option.iconType}`}
                    />
                    <div className="dbo-report-access-panel__info">
                      <span className="dbo-report-access-panel__card-title">
                        {formatMessage({ id: option.titleKey })}
                      </span>
                      <span className="dbo-report-access-panel__card-desc">
                        {formatMessage({ id: option.descKey })}
                      </span>
                    </div>
                  </div>
                </Radio>
              ))}
            </Radio.Group>

            {permissionLevel === 'partial' && (
              <div className="dbo-report-access-panel__users">
                <div className="dbo-report-access-panel__users-row">
                  <div className="dbo-report-access-panel__users-label">
                    {formatMessage({ id: 'Selected Users' })}
                  </div>
                  <div className="dbo-report-access-panel__users-field">
                    <PermissionUserInput
                      value={selectedUsers}
                      onChange={handleSelectedUsersChange}
                      onSearch={searchUsers}
                      searchOptions={searchOptions}
                      searchLoading={searchLoading}
                      hasMore={hasMore}
                      loadMoreLoading={loadMoreLoading}
                      onLoadMore={loadMore}
                      isError={isError}
                      onClearError={() => setIsError(false)}
                    />
                    {isError && (
                      <div className="dbo-report-access-panel__users-error">
                        {formatMessage({ id: 'Cannot be empty' })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardCommon>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          loading={saving}
          disabled={loading || isError}
          onClick={handleSave}
        >
          {formatMessage({ id: 'Confirm' })}
        </Button>
        <Button type="weak" onClick={onClose}>
          {formatMessage({ id: 'Cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export { default as PermissionCapsule } from './PermissionCapsule';
export {
  default as ReportPermissionProvider,
  useReportPermissionNotify,
} from './ReportPermissionProvider';
export { default as ReportOwnerSegment } from './ReportOwnerSegment';
export type { IReportOwnerSegmentProps } from './ReportOwnerSegment';
export {
  DEFAULT_REPORT_OWNER,
  MAX_AVATAR_STACK,
  PERMISSION_LEVEL_ICON,
  PERMISSION_LEVEL_OPTIONS,
  SAVED_LIST_DEFAULT_REPORT_OWNER,
  parseReportOwnerQuery,
  buildSavedAnalysisListUrl,
  VISIBLE_AVATAR_COUNT,
} from './constants';
export type { TReportOwner } from './constants';
export {
  buildPermissionTarget,
  parseReportPermissionTarget,
} from './utils/permissionNotification';
